// English comments only.
// Robust helpers for building absolute image URLs safely with runtime override.

import {getImageBaseFromRuntime} from "./runtime-config";

// Do NOT cache base at module load. Read dynamically so runtime changes apply.
function getBase(): string {
  // 1) runtime override (no rebuild needed)
  const rt = getImageBaseFromRuntime();
  if (rt) return rt.replace(/\/+$/, "");

  // 2) build-time fallback
  const raw = (import.meta as any).env?.VITE_BASE_IMAGE_SRC || "";
  return String(raw).replace(/\/+$/, "");
}

/** Remove scheme and leading slashes for comparison. */
function stripProtocol(u: string): string {
  return u.replace(/^[a-z]+:\/\//i, "").replace(/^\/+/, "");
}

/** Is already absolute (http/https/ protocol-relative // / data / blob)? */
function isAbsolute(u: string): boolean {
  if (/^(?:https?:)?\/\//i.test(u)) return true;
  if (/^(data:|blob:)/i.test(u)) return true;
  return false;
}

/** Ensure path starts with a single leading slash (root-relative). */
function ensureRootRelative(p: string): string {
  return "/" + p.replace(/^\/+/, "");
}

/** Join base URL and relative path with a single slash. */
function joinUrl(base: string, rel: string): string {
  const b = base.replace(/\/+$/, "");
  const r = rel.replace(/^\/+/, "");
  return `${b}/${r}`;
}

/** Heuristic: app-local assets that must NOT use the external image base. */
function isLocalAppAsset(p: string): boolean {
  const s = p.replace(/^\/+/, "").toLowerCase();
  if (s.startsWith("storage/")) return true;
  if (s.startsWith("media/")) return true;
  if (s.startsWith("uploads/")) return true;
  if (s.startsWith("channels/") || s.includes("/channels/")) return true;
  return false;
}

/**
 * Returns a safe absolute URL for images.
 * Priority:
 * 1) If input absolute => return as-is.
 * 2) If malformed like "/https://..." => fix and return absolute.
 * 3) If local-app asset => root-relative (stay on app domain).
 * 4) Else prefix with runtime base (or build-time fallback).
 */
export function buildImageSrc(input?: string | null): string {
  if (!input) return "";
  let trimmed = String(input).trim();

  // Fix malformed "/https://..." or "/http://..." cases early
  if (/^\/+https?:\/\//i.test(trimmed)) {
    trimmed = trimmed.replace(/^\/+/, "");
  }

  if (isAbsolute(trimmed)) return trimmed;

  if (isLocalAppAsset(trimmed)) {
    return ensureRootRelative(trimmed);
  }

  const IMG_BASE = getBase();
  if (IMG_BASE) {
    const inNoProto = stripProtocol(trimmed).toLowerCase();
    const baseNoProto = stripProtocol(IMG_BASE).toLowerCase();
    if (inNoProto.startsWith(baseNoProto)) {
      return trimmed; // already prefixed with our base
    }
    return joinUrl(IMG_BASE, trimmed);
  }

  // No base provided: keep as site-relative
  return ensureRootRelative(trimmed);
}

/** Optional helper: first non-empty candidate -> absolute URL. */
export function buildFromCandidates(
  ...candidates: Array<string | null | undefined>
): string {
  for (const c of candidates) {
    if (c && String(c).trim() !== "") {
      return buildImageSrc(c);
    }
  }
  return "";
}
